import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class GlobalInputSecurityService {
  private readonly blockedPatterns: RegExp[] = [
    /<[^>]*>/gim,
    /on\w+\s*=/gim,
    /javascript\s*:/gim,
    /data\s*:\s*text\/html/gi,
    /<|>/g,
  ];

  private readonly excludedInputTypes = new Set([
    'checkbox',
    'radio',
    'file',
    'button',
    'submit',
    'reset',
    'range',
    'color',
    'date',
    'datetime-local',
    'month',
    'time',
    'week',
  ]);

  private readonly unlisteners: Array<() => void> = [];
  private isInitialized = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly rendererFactory: RendererFactory2,
    private readonly ngZone: NgZone,
  ) {}

  init(): void {
    if (this.isInitialized || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.isInitialized = true;
    const renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);

    this.ngZone.runOutsideAngular(() => {
      this.unlisteners.push(
        renderer.listen(this.document, 'input', (event: Event) => this.sanitizeTarget(event)),
      );
      this.unlisteners.push(
        renderer.listen(this.document, 'change', (event: Event) => this.sanitizeTarget(event)),
      );
      this.unlisteners.push(
        renderer.listen(this.document, 'paste', (event: Event) => {
          setTimeout(() => this.sanitizeTarget(event), 0);
        }),
      );
    });
  }

  destroy(): void {
    while (this.unlisteners.length) {
      const unlisten = this.unlisteners.pop();
      unlisten?.();
    }
    this.isInitialized = false;
  }

  private sanitizeTarget(event: Event): void {
    const target = event.target;
    if (!this.isSupportedTarget(target)) {
      return;
    }

    const currentValue = target.value;
    const sanitizedValue = this.sanitizeValue(currentValue);

    if (currentValue !== sanitizedValue) {
      target.value = sanitizedValue;
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  private sanitizeValue(value: string): string {
    let sanitized = value;

    for (const pattern of this.blockedPatterns) {
      sanitized = sanitized.replace(pattern, '');
    }

    return sanitized;
  }

  private isSupportedTarget(
    target: EventTarget | null,
  ): target is HTMLInputElement | HTMLTextAreaElement {
    if (target instanceof HTMLTextAreaElement) {
      return true;
    }

    if (!(target instanceof HTMLInputElement)) {
      return false;
    }

    const inputType = (target.type || 'text').toLowerCase();
    return !this.excludedInputTypes.has(inputType);
  }
}